import { useCallback, useEffect, useMemo, useRef } from 'react'

export type BackoffOptions = {
  maxAttempts: number
  baseDelayMs: number
  maxDelayMs: number
  onRetryScheduled?: (attempt: number, delayMs: number) => void
  onGiveUp?: () => void
}

export const backoffDelayMs = (
  attempt: number,
  {
    baseDelayMs,
    maxDelayMs,
  }: Pick<BackoffOptions, 'baseDelayMs' | 'maxDelayMs'>,
): number => Math.min(baseDelayMs * 2 ** Math.max(attempt - 1, 0), maxDelayMs)

export const useBackoffRetry = (options: BackoffOptions) => {
  const attemptsRef = useRef(0)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const optionsRef = useRef(options)

  useEffect(() => {
    optionsRef.current = options
  })

  useEffect(() => () => clearTimeout(timeoutRef.current), [])

  const cancel = useCallback(() => {
    clearTimeout(timeoutRef.current)
  }, [])

  const reset = useCallback(() => {
    clearTimeout(timeoutRef.current)
    attemptsRef.current = 0
  }, [])

  const schedule = useCallback((run: () => void) => {
    clearTimeout(timeoutRef.current)
    attemptsRef.current += 1

    const { maxAttempts, onRetryScheduled, onGiveUp } = optionsRef.current
    if (attemptsRef.current > maxAttempts) {
      onGiveUp?.()
      return
    }

    const delay = backoffDelayMs(attemptsRef.current, optionsRef.current)
    onRetryScheduled?.(attemptsRef.current, delay)
    timeoutRef.current = setTimeout(run, delay)
  }, [])

  return useMemo(() => ({ schedule, reset, cancel }), [schedule, reset, cancel])
}
